import { TILE_SIZE } from '@dreamgame/shared';
import { useEffect, useRef } from 'preact/hooks';
import { tv } from 'tailwind-variants';
import { centerOnPosition, centerOnPositionWithZoom } from '../../render/Renderer';
import { ownedPawns, type PawnData } from '../../state/entities';
import {
  clearColonistPortraitRects,
  isSelected,
  registerColonistPortrait,
  selectAllPawns,
  selectedIds,
  selectPawn,
} from '../../state/selection';

// ============================================================================
// Constants
// ============================================================================

const PAWN_STATES = ['Idle', 'Moving', 'Working', 'Fighting', 'Resting', 'Eating'] as const;

const stateColors: Readonly<Record<number, string>> = {
  0: 'bg-emerald-500', // Idle
  1: 'bg-yellow-500', // Walking
  2: 'bg-red-500', // Working
  3: 'bg-red-700', // Fighting
  4: 'bg-blue-500', // Resting
  5: 'bg-orange-500', // Eating
};

const healthColors = {
  high: 'bg-gradient-to-r from-emerald-600 to-emerald-500',
  medium: 'bg-gradient-to-r from-amber-600 to-amber-500',
  low: 'bg-gradient-to-r from-red-600 to-red-500',
} as const;

// ============================================================================
// Styles
// ============================================================================

const colonistBar = tv({
  slots: {
    root: [
      'fixed top-[0.8em] left-1/2 -translate-x-1/2 z-[900]',
      'flex gap-[0.55em]',
      'p-[0.5em]',
      'animate-slide-down',
      'select-none',
      'pointer-events-none', // Let world drag pass through
      'text-[clamp(0.95rem,0.9rem+0.35vw,1.15rem)]',
    ],
    portrait: [
      'relative w-[4.5em] h-[5.75em] flex flex-col items-center justify-center',
      'bg-slate-900/40',
      'border border-slate-600/30 rounded-[0.5em]',
      'cursor-pointer transition-all duration-150',
      'hover:border-indigo-400/60 hover:bg-slate-800/50',
      'group p-[0.4em]',
      'pointer-events-auto', // But portraits are clickable
    ],
    portraitSelected: [
      'border-indigo-400 bg-indigo-900/40',
      'shadow-[0_0_12px_rgba(99,102,241,0.6)]',
      'ring-2 ring-indigo-400/50 ring-offset-1 ring-offset-transparent',
    ],
    pawnSprite: ['w-[2.8em] h-[2.8em]', 'image-rendering-pixelated', '[image-rendering:pixelated]'],
    pawnName: [
      'text-[0.62em] text-slate-200 truncate w-full text-center mt-[0.32em]',
      'leading-tight font-medium',
    ],
    healthBar: [
      'absolute bottom-0 left-0 right-0 h-[0.35em]',
      'bg-slate-900/60 rounded-b-lg overflow-hidden',
    ],
    healthFill: 'h-full transition-all duration-300',
    stateIndicator: [
      'absolute -top-[0.22em] -right-[0.22em] w-[0.95em] h-[0.95em]',
      'rounded-full border-2 border-slate-900/80',
    ],
    downedOverlay: ['absolute inset-0 rounded-lg', 'bg-red-600/40', 'pointer-events-none'],
    tooltip: [
      'absolute bottom-full left-1/2 -translate-x-1/2 mb-[0.55em]',
      'bg-slate-900/95 border border-slate-600/60 rounded-[0.4em]',
      'px-[0.7em] py-[0.45em] whitespace-nowrap',
      'opacity-0 group-hover:opacity-100 transition-opacity duration-150',
      'pointer-events-none z-10',
      'shadow-lg',
    ],
    tooltipName: 'text-[0.78em] font-semibold text-indigo-100',
    tooltipState: 'text-[0.68em] text-slate-400',
    number: 'text-[0.62em] text-slate-300/80 font-mono absolute top-[0.12em] left-[0.35em]',
  },
});

// ============================================================================
// Helpers
// ============================================================================

const getHealthColor = ({ percent }: { percent: number }): string => {
  if (percent > 60) {
    return healthColors.high;
  }
  if (percent > 30) {
    return healthColors.medium;
  }
  return healthColors.low;
};

// ============================================================================
// Components
// ============================================================================

interface ColonistPortraitProps {
  pawn: PawnData;
  index: number;
}

const ColonistPortrait = ({ pawn, index }: ColonistPortraitProps) => {
  const styles = colonistBar();
  const selected = isSelected({ networkId: pawn.networkId });
  const healthPercent = (pawn.health / pawn.maxHealth) * 100;
  const isDowned = pawn.isDowned === 1;
  const stateText = isDowned ? 'Downed' : (PAWN_STATES[pawn.state] ?? 'Unknown');
  const stateColor = isDowned ? 'bg-slate-800' : (stateColors[pawn.state] ?? 'bg-slate-500');
  const firstName = pawn.name.split(' ')[0] ?? pawn.name;

  const handleClick = (e: MouseEvent): void => {
    e.stopPropagation();
    const additive = e.shiftKey || e.ctrlKey || e.metaKey;
    selectPawn({ networkId: pawn.networkId, additive });
  };

  const handleDoubleClick = (e: MouseEvent): void => {
    e.stopPropagation();
    const worldX = pawn.x * TILE_SIZE + TILE_SIZE / 2;
    const worldY = pawn.y * TILE_SIZE + TILE_SIZE / 2;
    centerOnPositionWithZoom({ x: worldX, y: worldY, zoom: 1.5 });
    selectPawn({ networkId: pawn.networkId, additive: false });
  };

  const handleKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const additive = e.shiftKey || e.ctrlKey || e.metaKey;
      selectPawn({ networkId: pawn.networkId, additive });
    }
  };

  const portraitClass = [styles.portrait(), selected ? styles.portraitSelected() : ''].join(' ');

  return (
    <button
      type="button"
      class={portraitClass}
      data-pawn-id={pawn.networkId}
      onClick={handleClick}
      onDblClick={handleDoubleClick}
      onKeyDown={handleKeyDown}
    >
      {isDowned && <div class={styles.downedOverlay()} />}
      <span class={styles.number()}>{index + 1}</span>

      <img
        src="/pawns/front_idle.png"
        alt={pawn.name}
        class={styles.pawnSprite()}
        draggable={false}
      />

      <span class={styles.pawnName()}>{firstName}</span>

      <div class={`${styles.stateIndicator()} ${stateColor}`} />

      <div class={styles.healthBar()}>
        <div
          class={`${styles.healthFill()} ${getHealthColor({ percent: healthPercent })}`}
          style={{ width: `${healthPercent}%` }}
        />
      </div>

      <div class={styles.tooltip()}>
        <div class={styles.tooltipName()}>{pawn.name}</div>
        <div class={styles.tooltipState()}>
          {stateText} - HP: {Math.round(pawn.health)}/{pawn.maxHealth}
        </div>
      </div>
    </button>
  );
};

export const ColonistBar = () => {
  const pawns = ownedPawns.value;
  const containerRef = useRef<HTMLDivElement>(null);

  // Force re-render when selection changes
  const _selection = selectedIds.value;

  // Update all portrait rects at once - single function, not per-portrait
  const updateAllPortraitRects = (): void => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const portraits = container.querySelectorAll('[data-pawn-id]');
    for (const portrait of portraits) {
      const pawnIdAttr = portrait.getAttribute('data-pawn-id');
      if (pawnIdAttr) {
        const pawnId = parseInt(pawnIdAttr, 10);
        registerColonistPortrait({ networkId: pawnId, rect: portrait.getBoundingClientRect() });
      }
    }
  };

  // Single resize listener at parent level (debounced)
  useEffect(() => {
    let resizeTimeout: ReturnType<typeof setTimeout> | null = null;

    const handleResize = (): void => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      resizeTimeout = setTimeout(updateAllPortraitRects, 100);
    };

    // Initial registration after mount
    updateAllPortraitRects();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      clearColonistPortraitRects();
    };
  }, [pawns.length]); // Re-run when pawn count changes

  // Keyboard shortcuts
  useEffect(() => {
    const isInputElement = (target: HTMLElement): boolean =>
      target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';

    const handleSelectAll = (e: KeyboardEvent): boolean => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        e.preventDefault();
        selectAllPawns();
        return true;
      }
      return false;
    };

    const handleNumberKey = (e: KeyboardEvent): void => {
      const keyNum = parseInt(e.key, 10);
      if (keyNum >= 1 && keyNum <= 9) {
        const pawn = ownedPawns.value[keyNum - 1];
        if (pawn) {
          const alreadySelected = isSelected({ networkId: pawn.networkId });
          if (alreadySelected && !e.shiftKey) {
            // Re-selecting same pawn: pan camera to center on it
            const worldX = pawn.x * TILE_SIZE + TILE_SIZE / 2;
            const worldY = pawn.y * TILE_SIZE + TILE_SIZE / 2;
            centerOnPosition({ x: worldX, y: worldY });
          } else {
            selectPawn({ networkId: pawn.networkId, additive: e.shiftKey });
          }
        }
      }
    };

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (isInputElement(e.target as HTMLElement)) {
        return;
      }
      if (!handleSelectAll(e)) {
        handleNumberKey(e);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (pawns.length === 0) {
    return null;
  }

  const styles = colonistBar();

  return (
    <div ref={containerRef} class={styles.root()}>
      {pawns.map((pawn, index) => (
        <ColonistPortrait key={pawn.networkId} pawn={pawn} index={index} />
      ))}
    </div>
  );
};
